import { Fragment } from "react";
import { NavLink, useSearchParams } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { useTimestamp } from "../../../hooks/useTimestamp";
import useTranslation, { Translate } from "../../../hooks/useTranslation";
import { TextSkeleton } from "../../Skeletons";
import { txStrReplacer as replacer } from "../lib";
import { redDot } from "../SubscriptionButtons/SubscriptionButtons";

export const SubscriptionTabButton = () => {
    const { lightboxEvent } = useYardActionEvents();
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();

    const T = useTranslation();
    const userFundsTxt = T("[name]'s funds").result;

    const loading = useFCSubscriptionStore((state) => state.loadingUser);
    const user = useFCSubscriptionStore((state) => state.user);
    const setRetrieveFunds = useFCSubscriptionStore(
        (state) => state.setRetrieveFunds,
    );
    const isUpdated = useFCSubscriptionStore(
        (state) => state.isUpdatedSubAsset,
    );
    const userSubscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const updates = useFCSubscriptionStore((state) => state.updates);
    const key = useTimestamp(updates);

    const hasUpdates = userSubscriptions.some(isUpdated);

    if (loading) {
        return (
            <div className="fund-centre-tab appearance-none sm:absolute sm:right-4 sm:!mr-0">
                <LoggedOutUserIcon className="fill-taupe-grey" />
                <TextSkeleton width={"My Funds".length} />
            </div>
        );
    }

    const content = (
        <Fragment key={key}>
            {user ? <LoggedInUserIcon /> : <LoggedOutUserIcon />}
            <span className={cx(hasUpdates && "relative")}>
                {user ? (
                    <span
                        className={cx(
                            hasUpdates && `${redDot} before:-right-1`,
                        )}
                    >
                        <span className="pii hidden lg:inline">
                            {replacer(userFundsTxt, {
                                name: user.firstName,
                            })}
                        </span>
                        <span className="lg:hidden">
                            <Translate>My funds</Translate>
                        </span>
                    </span>
                ) : (
                    <Translate>My funds</Translate>
                )}
            </span>
        </Fragment>
    );

    if (user) {
        return (
            <NavLink
                id="my-funds"
                className={({ isActive }) =>
                    cx(
                        "fund-centre-tab sm:absolute sm:right-4 sm:!mr-0",
                        isActive && "selected",
                    )
                }
                to={"/my-funds" + (search ? `?${search}` : "")}
                onClick={() => {
                    const tabs = document.querySelectorAll<HTMLAnchorElement>(
                        "a.fund-centre-tab:not(#my-funds)",
                    );
                    tabs.forEach((tab) => {
                        tab.classList.remove("selected");
                    });
                }}
            >
                {content}
            </NavLink>
        );
    } else {
        return (
            <button
                className="fund-centre-tab appearance-none sm:absolute sm:right-4 sm:!mr-0"
                onClick={() => {
                    setRetrieveFunds(true);
                    lightboxEvent("Subscriptions and watchlists");
                }}
            >
                {content}
            </button>
        );
    }
};

const LoggedInUserIcon = () => (
    <div className="fund-centre-tab-icon p-1 !filter-none">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.816 22">
            <circle
                id="Ellipse_398"
                data-name="Ellipse 398"
                cx="5"
                cy="5"
                r="5"
                transform="translate(6.5 1)"
                fill="#ffd500"
            />
            <path
                id="Path_1435"
                data-name="Path 1435"
                d="M6.559,2.444,11.572,3.6l4.84-1.153L23,10H0Z"
                transform="translate(0.5 11.83)"
                fill="#ffd500"
            />
            <g id="person">
                <path
                    id="Icon"
                    d="M18.9,18.339l.573.173a12.023,12.023,0,0,1,8.071,8.216L27.908,28H4.092l.36-1.272a12.023,12.023,0,0,1,8.071-8.216l.573-.173,2.9,2.9Zm.553,2.275L16,24.071l-3.458-3.458A10.035,10.035,0,0,0,6.832,26H25.168A10.036,10.036,0,0,0,19.458,20.614ZM16,6a6,6,0,1,1-6,6A6,6,0,0,1,16,6Zm0,2a4,4,0,1,0,4,4A4,4,0,0,0,16,8Z"
                    transform="translate(-4.092 -6)"
                    fill="#575757"
                />
            </g>
        </svg>
    </div>
);

const LoggedOutUserIcon = ({
    className = "fill-battleship-grey",
}: {
    className?: string;
}) => (
    <div className="fund-centre-tab-icon p-1">
        <svg
            id="person"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 23.816 22"
            className={className}
        >
            <path
                id="Icon"
                d="M18.9,18.339l.573.173a12.023,12.023,0,0,1,8.071,8.216L27.908,28H4.092l.36-1.272a12.023,12.023,0,0,1,8.071-8.216l.573-.173,2.9,2.9Zm.553,2.275L16,24.071l-3.458-3.458A10.035,10.035,0,0,0,6.832,26H25.168A10.036,10.036,0,0,0,19.458,20.614ZM16,6a6,6,0,1,1-6,6A6,6,0,0,1,16,6Zm0,2a4,4,0,1,0,4,4A4,4,0,0,0,16,8Z"
                transform="translate(-4.092 -6)"
            />
        </svg>
    </div>
);
